import * as z from 'zod'
import { zodQs } from '../utils'

export const NotificationType = {
    DocumentError: 'document_error',
    ApiKeyExpiring: 'api_key_expiring',
    Organization: 'organization',
    PasswordResetRequested: 'password_reset_requested',
    /** Two-step verification turned on, off, or reset by an administrator. */
    TwoFactor: 'two_factor',
    /** A sync took documents out because the remote no longer has them. */
    DocumentSyncRemoved: 'document_sync_removed',
    /** A web source took out pages its sitemap no longer lists. */
    WebSourcePagesRemoved: 'web_source_pages_removed',
    /** A web source held back a removal too large to trust without a person. */
    WebSourceRemovalHeld: 'web_source_removal_held',
    ChatWarning: 'chat_warning',
    ChatBlocked: 'chat_blocked',
    Generic: 'generic',
} as const

export type NotificationType =
    (typeof NotificationType)[keyof typeof NotificationType]

/**
 * Group key of the admins' notification about a flagged conversation: one row
 * per chat and admin, bumped by every later analysis of that chat.
 */
export const chatAnalysisNotificationGroupKey = (chatId: string): string =>
    `chat_analysis:${chatId}`

/**
 * Title of the admins' notification about a flagged conversation. It tells a
 * block from a warning on a stored row better than `type` does: a group bump
 * rewrites the title, the body and the metadata, never the type, so a chat
 * warned first and blocked later keeps `type = chat_warning`.
 */
export const chatAnalysisNotificationTitle = (blocked: boolean): string =>
    blocked ? 'Conversation blocked' : 'Conversation warning'

/**
 * Body of the admins' notification about a flagged conversation
 * (`chat_warning` or `chat_blocked`). The writer quotes the chat's title; the
 * GDPR erasure (`eraseConversationRows`) rebuilds the body without it, from the
 * same sentences, so the notification and its link to the chat survive with
 * nothing the user wrote. Migration `erase_chat_residuals` holds a SQL copy
 * of these sentences for the rows erased before it.
 */
export function chatAnalysisNotificationBody({
    blocked,
    riskScore,
    chatTitle,
}: {
    blocked: boolean
    riskScore?: number
    chatTitle?: string
}): string {
    const subject =
        chatTitle === undefined
            ? 'The conversation'
            : `The conversation "${chatTitle}"`
    const event = blocked
        ? 'has been automatically blocked.'
        : 'received a warning.'
    const risk = riskScore === undefined ? '' : ` Risk score: ${riskScore}.`
    return `${subject} ${event}${risk}`
}

const NotificationTypeSchema = z.string() as z.ZodType<string>

export const NotificationSchema = z.object({
    id: z.string(),
    organizationId: z.string().nullable(),
    organizationName: z.string().nullable().optional(),
    userId: z.string().nullable(),
    userEmail: z.string().nullable().optional(),
    type: NotificationTypeSchema,
    title: z.string(),
    body: z.string().nullable(),
    groupKey: z.string().nullable(),
    groupCount: z.number().int(),
    metadata: z.record(z.string(), z.unknown()).nullable(),
    scheduledAt: z.coerce.date().nullable(),
    readAt: z.coerce.date().nullable(),
    deletedAt: z.coerce.date().nullable(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
})

export const NotificationsQuerystringSchema = z.object({
    ...zodQs.pagination(),
    ...zodQs.sort(['createdAt', 'updatedAt', 'scheduledAt']),
    ...zodQs.filters(['type', 'readAt', 'scheduledAt']), // scheduledAt: 'future' | 'past' | omit for all
    ...zodQs.ids(),
})

export const NotificationMarkReadSchema = NotificationSchema.pick({
    id: true,
    organizationId: true,
    userId: true,
})

export const NotificationDeleteSchema = z.object({
    id: z.string().uuid(),
})

export const NotificationCreateDtoSchema = z.object({
    organizationId: z.uuid().nullable().optional(),
    userId: z.uuid().nullable().optional(),
    type: NotificationTypeSchema.default(NotificationType.Generic),
    title: z.string().min(1).max(500),
    body: z.string().max(2000).nullable().optional(),
    groupKey: z.string().max(255).nullable().optional(),
    metadata: z.record(z.string(), z.unknown()).nullable().optional(),
    scheduledAt: z.coerce.date().nullable().optional(),
})

export const FrontendNotificationTypes = [
    NotificationType.Generic,
    NotificationType.Organization,
] as const

export const NotificationCreateFrontendDtoSchema =
    NotificationCreateDtoSchema.extend({
        type: z
            .enum(FrontendNotificationTypes)
            .default(NotificationType.Generic),
    })

export type NotificationCreateFrontendDto = z.infer<
    typeof NotificationCreateFrontendDtoSchema
>

export const NotificationUnreadCountSchema = z.object({
    count: z.number().int(),
})

export const UserNotificationSchema = z.object({
    id: z.string(),
    notificationId: z.string(),
    userId: z.string(),
    readAt: z.coerce.date().nullable(),
    deletedAt: z.coerce.date().nullable(),
    createdAt: z.coerce.date(),
})

export type Notification = z.infer<typeof NotificationSchema>
export type NotificationsQuerystring = z.infer<
    typeof NotificationsQuerystringSchema
>
export type NotificationMarkRead = z.infer<typeof NotificationMarkReadSchema>
export type NotificationCreateDto = z.infer<typeof NotificationCreateDtoSchema>
export type NotificationUnreadCount = z.infer<
    typeof NotificationUnreadCountSchema
>
export type UserNotification = z.infer<typeof UserNotificationSchema>
export type NotificationDelete = z.infer<typeof NotificationDeleteSchema>
